import { CSElement } from './core/CSElement';

/**
 * CSElement Browser - Браузерная версия библиотеки для работы с графовыми структурами
 * Исключает Node.js специфичные модули для полной совместимости с браузером
 */

// Основной класс
export { CSElement };
export default CSElement;

// Базовые типы и интерфейсы
export {
  ICSElement,
  ICSElementPattern,
  ElementOptions,
  ElementValue,
  ElementEvent,
  ElementEventType,
  SerializationOptions,
  SerializedElement,
  ValidationRule,
  ValidationResult,
  ValidationError,
  QuerySelector,
  PerformanceMetrics
} from './types/interfaces';

// Типы плагинов (базовые)
export {
  CSElementPlugin,
  LifecycleHooks,
  MiddlewareFunction,
  MiddlewareContext,
  MiddlewareConfig,
  MiddlewareResult,
  MiddlewareStats,
  MiddlewarePriority,
  AdvancedMiddlewareManager,
  PluginManager as IPluginManager,
  PluginOptions,
  PluginInfo,
  PluginEvent
} from './types/plugin-interfaces';

// Типы визуализации
export {
  VisualizationFormat,
  LayoutType,
  TreeDirection,
  NodeStyle,
  EdgeStyle,
  VisualizationOptions,
  VisualizationResult,
  VisualizationNode,
  VisualizationEdge,
  VisualizationGraph,
  VisualizationEngine,
  VisualizationManager as IVisualizationManager,
  VisualizationEvent,
  VisualizationTheme,
  FileExporter
} from './types/visualization-interfaces';

// Утилиты (совместимые с браузером)
export { AsyncLock } from './utils/AsyncLock';
export { generateId, deepClone, delay } from './utils/helpers';
export { ElementValidator, defaultValidator, ValidationType } from './utils/Validator';
export { QueryEngine, SelectorBuilder, CommonSelectors } from './utils/QueryEngine';
export { TypeScriptGenerator, generateTypesFromSchemas } from './utils/TypeScriptGenerator';

// Batch Operations
export { BatchManager } from './batch/BatchManager';
export { DefaultBatchOperationFactory, DefaultBatchBuilder, BatchOperations } from './batch/BatchOperationFactory';
export {
  BatchOperationType,
  BatchPriority,
  BatchExecutionStrategy,
  BatchErrorMode,
  IBatchManager,
  BatchConfig,
  BatchResult,
  BatchProgress,
  BatchEventListener,
  BatchManagerStatistics,
  BatchOperationFactory,
  BatchBuilder
} from './types/batch-interfaces';
export type {
  BatchOperation,
  BatchExecutionContext,
  BatchOperationResult,
  BatchValidationResult,
  BatchStatistics
} from './types/batch-interfaces';

// Blueprint System
export { Blueprint } from './blueprint/Blueprint';
export { BlueprintBuilder } from './blueprint/BlueprintBuilder';
export { BlueprintManager } from './blueprint/BlueprintManager';
export * from './types/blueprint-interfaces';

// Система плагинов
export { PluginManager } from './core/PluginManager';
export { AdvancedMiddlewareManagerImpl } from './core/AdvancedMiddlewareManager';

// Система персистентности (только совместимые с браузером адаптеры)
export * from './types/persistence-interfaces';
export { MemoryStorageAdapter } from './persistence/MemoryStorageAdapter';
export { IndexedDBAdapter } from './persistence/IndexedDBAdapter';
export { LocalForageAdapter } from './persistence/LocalForageAdapter';
export { PersistenceManagerImpl } from './persistence/PersistenceManagerImpl';

// Система транзакций
export * from './types/transaction-interfaces';
export { LockManagerImpl } from './transactions/LockManagerImpl';
export { TransactionManagerImpl } from './transactions/TransactionManagerImpl';

// Встроенные плагины (только совместимые с браузером)
export { VisualizationPlugin, visualizationPlugin } from './plugins/VisualizationPlugin';
export { ValidationPlugin, validationPlugin } from './plugins/ValidationPlugin';
export { CachePlugin, cachePlugin } from './plugins/CachePlugin';
export { MetricsPlugin, metricsPlugin } from './plugins/MetricsPlugin';
export { TransformPlugin, transformPlugin } from './plugins/TransformPlugin';
export { DevToolsPlugin, devToolsPlugin } from './plugins/DevToolsPlugin';
export { LoggingPlugin } from './plugins/LoggingPlugin';
export { AnalyticsPlugin } from './plugins/AnalyticsPlugin';

// Система визуализации
export { VisualizationManager } from './visualization/VisualizationManager';
export { AsciiEngine } from './visualization/engines/AsciiEngine';
export { SvgEngine } from './visualization/engines/SvgEngine';
export { HtmlEngine } from './visualization/engines/HtmlEngine';

// Система DevTools
export { DevToolsManager } from './devtools/DevToolsManager';
export { ElementInspector } from './devtools/ElementInspector';
export { PerformanceProfiler } from './devtools/PerformanceProfiler';
export * from './types/devtools-interfaces';

// Система истории изменений
export { HistoryManagerImpl } from './history/HistoryManagerImpl';
export * from './types/history-interfaces';

// Система реактивности и computed свойств
export { ReactivityManagerImpl } from './reactivity/ReactivityManagerImpl';
export * from './types/reactivity-interfaces';

// Система Live queries (реактивных запросов)
export { LiveQueryManagerImpl, LiveQueryBuilderImpl } from './live-queries/LiveQueryManagerImpl';
export * from './types/live-query-interfaces';

// Система алгоритмов работы с графами
export { GraphAlgorithmsManagerImpl } from './graph/GraphAlgorithmsManagerImpl';
export * from './types/graph-interfaces';

// Система State Machine
export { StateMachine } from './state-machine/StateMachine';
export { StateMachineManagerImpl } from './state-machine/StateMachineManager';
export { StateMachineIntegration } from './core/StateMachineIntegration';
export * from './types/state-machine-interfaces';

// Система типизированных элементов
export { TypedElement } from './typed-elements/TypedElement';
export { TypedElementManager, defaultTypedElementManager } from './typed-elements/TypedElementManager';
export * from './types/typed-elements-interfaces';

// Система миграций
export { MigrationManager } from './migration/MigrationManager';
export { MemoryMigrationStorage } from './migration/MemoryMigrationStorage';
export { VersionUtils } from './migration/VersionUtils';
export { MigrationBuilder, createMigration } from './migration/MigrationBuilder';
export { 
  MigrationRegistry, 
  Migration, 
  Up, 
  Down, 
  CanApply, 
  CanRollback, 
  Validate, 
  DependsOn, 
  Type,
  BaseMigration,
  SchemaMigration,
  DataMigration,
  MigrationUtils
} from './migration/MigrationDecorators';
export { MigrationFactory, migrationFactory } from './migration/MigrationFactory';
export { MigrationRunner } from './migration/MigrationRunner';
export { MigrationScheduler } from './migration/MigrationScheduler';
export { MigrationLogger } from './migration/MigrationLogger';
export { MigrationSystem } from './migration/MigrationSystem';
export * from './types/migration-interfaces';

// Система навигации по элементам
export { ElementNavigation } from './core/ElementNavigation';

// Система реестра элементов
export { ElementRegistry } from './core/ElementRegistry';

// Система сериализации элементов
export { ElementSerializer } from './core/ElementSerializer';

// Система реестра сервисов - ServiceRegistry может быть недоступен в браузере
// export { ServiceRegistry } from './core/ServiceRegistry';

// Diff Engine
export { DiffEngine } from './diff/DiffEngine';

// Реэкспорт для удобства
export { EventEmitter } from 'eventemitter3';

// Для обратной совместимости экспортируем CSElement как ElementBuilder и ElementSpace
export const ElementBuilder = CSElement;
export const ElementSpace = CSElement;

// Браузерные заглушки для React хуков (если React доступен)
let reactHooks: any = null;
try {
  // Пытаемся импортировать React хуки только если React доступен
  if (typeof window !== 'undefined' && (window as any).React) {
    reactHooks = require('./react/hooks');
  }
} catch (e) {
  // React не доступен, игнорируем
}

// React хуки экспортируются динамически только если React доступен
export const useCSElement = reactHooks?.useCSElement;
export const useCSElementValue = reactHooks?.useCSElementValue;
export const useCSElementChildren = reactHooks?.useCSElementChildren;
export const useCSElementQuery = reactHooks?.useCSElementQuery;
export const useCSElementHistory = reactHooks?.useCSElementHistory;
export const useCSElementBatch = reactHooks?.useCSElementBatch;
export const useCSElementTransaction = reactHooks?.useCSElementTransaction;
export const useCSElementValidation = reactHooks?.useCSElementValidation;
export const useCSElementPersistence = reactHooks?.useCSElementPersistence;
export const useCSElementMetrics = reactHooks?.useCSElementMetrics;
export const useCSElementReactivity = reactHooks?.useCSElementReactivity;
export const useCSElementLiveQuery = reactHooks?.useCSElementLiveQuery;
export const useCSElementStateMachine = reactHooks?.useCSElementStateMachine;
export const useCSElementMigration = reactHooks?.useCSElementMigration;
export const useCSElementVisualization = reactHooks?.useCSElementVisualization;
export const useCSElementDevTools = reactHooks?.useCSElementDevTools;
export const useCSElementTyped = reactHooks?.useCSElementTyped;
export const useCSElementBlueprint = reactHooks?.useCSElementBlueprint;
export const useCSElementGraph = reactHooks?.useCSElementGraph;
export const useCSElementService = reactHooks?.useCSElementService;
export const useCSElementNavigation = reactHooks?.useCSElementNavigation;

// Предупреждение о недоступных в браузере функциях
if (typeof window !== 'undefined') {
  console.warn('CSElement Browser: Некоторые функции недоступны в браузере (BackupPlugin, CompressionPlugin, SecurityPlugin, NetworkPlugin). Используйте import из cs-element/workers/nodejs для WorkerManager');
} 